import {
  Chip,
  column,
  Css,
  FilterDefs,
  Filters,
  GridColumn,
  GridTable,
  Pagination,
  RowStyles,
  ScrollableContent,
  simpleDataRows,
  SimpleHeaderAndData,
  Tag,
  toggleFilter,
  usePersistedFilter,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { createDevelopmentContractUrl, createProjectBidContractOverviewUrl } from "src/RouteUrls";
import {
  BidContractsTableV2_BidContractFragment,
  useBidContractsTableV2Query,
} from "src/components/bid-contracts/BidContractsTableV2.generated";
import { BidContractsFilter, BidScopeSource } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import useZodQueryString from "src/hooks/useZodQueryString";
import { bidHierarchyColumn } from "src/routes/bids/BidPackagesPage";
import { TableActions } from "src/routes/layout/TableActions";
import { useProjectContext } from "src/routes/projects/context/ProjectContext";
import { pageSchema } from "src/utils";
import { LoadingTable } from "../LoadingTable";
import { SearchBox } from "../SearchBox";
import { chipCell, dateCell } from "../gridTableCells";
import { getTagTypeFromStatus } from "./BidContractHeader";

export function BidContractsTableV2() {
  const project = useProjectContext();
  const { projectId } = useRouteParams<{ projectId: string }>();
  const [pageSettings, setPageSettings] = useZodQueryString(pageSchema);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const filterDefs: FilterDefs<BidContractsFilter> = useMemo(
    () => ({
      // If we are viewing project bid packages, include the toggle to show bids from all levels
      ...(project.id
        ? { includeRelatedProjectBids: toggleFilter({ label: "Show Bids from All Levels", defaultValue: true }) }
        : {}),
    }),
    [project],
  );
  const { filter, setFilter } = usePersistedFilter<BidContractsFilter>({
    storageKey: "bidContractsV2Filter",
    filterDefs,
  });
  const { includeRelatedProjectBids } = filter;
  const { loading, data } = useBidContractsTableV2Query({
    variables: {
      filter: {
        search: searchFilter,
        // If on a project route, load only the bid contracts for that project
        ...(projectId && { projects: [projectId] }),
        // If we are viewing subdivision bids, include child project bids toggle determines if Lot bids are included
        ...(project.__typename === "Subdivision"
          ? {
              scopeSource: includeRelatedProjectBids
                ? [BidScopeSource.Subdivision, BidScopeSource.Lot]
                : [BidScopeSource.Subdivision],
            }
          : { includeRelatedProjectBids }),
      },
      page: pageSettings,
    },
  });
  const columns = useMemo(() => createColumns(), []);
  const rowStyles: RowStyles<Row> = {
    data: {
      rowLink: (row) =>
        row.data.developments.nonEmpty
          ? createDevelopmentContractUrl(row.data.developments[0].id, row.data.latestRevision.id)
          : createProjectBidContractOverviewUrl(
              projectId ?? row.data.latestRevision.bidContract.projects[0].id,
              row.data.latestRevision.id,
            ),
    },
  };

  return (
    <>
      <TableActions>
        <Filters filter={filter} onChange={setFilter} filterDefs={filterDefs} />
        <SearchBox onSearch={setSearchFilter} />
      </TableActions>
      <ScrollableContent>
        {!loading ? (
          <>
            <GridTable
              columns={columns}
              rows={simpleDataRows(data?.bidContractsPage?.entities || [])}
              rowStyles={rowStyles}
              stickyHeader
              fallbackMessage="There aren't any bid contracts to display."
            />
            <Pagination
              page={[pageSettings, setPageSettings]}
              totalCount={data?.bidContractsPage?.pageInfo.totalCount || 0}
            />
          </>
        ) : (
          <LoadingTable />
        )}
      </ScrollableContent>
    </>
  );
}

type Row = SimpleHeaderAndData<BidContractsTableV2_BidContractFragment>;

const createColumns = (): GridColumn<Row>[] => [
  column<Row>({ header: "Trade Partner", data: ({ displayName }) => displayName, w: 2 }),
  column<Row>({
    header: "Version",
    data: (bc) => ({ content: bc.latestRevision.version, value: parseInt(bc.latestRevision.version || "0") }),
  }),
  column<Row>({
    header: "Cost Codes",
    data: ({ latestRevision }) => {
      const hasAward = latestRevision.costCodeAwards.nonEmpty;
      if (!hasAward) return chipCell(latestRevision.costCodes.map((cc) => cc.number));
      return (
        <div css={Css.df.gapPx(5).fww.$}>
          {latestRevision.costCodeAwards.map((c) => {
            const award = c.isPrimary ? "Primary" : c.isSecondary ? "Secondary" : "None";
            return (
              <div css={Css.df.fdr.gap1.aic.$} key={c.costCode.id}>
                <Chip text={c.costCode.number} xss={Css.mt0.$} />
                {award}
              </div>
            );
          })}
        </div>
      );
    },
    w: 1.2,
  }),
  column<Row>({
    header: "Hierarchy",
    data: ({ projects, scopeSource }) => bidHierarchyColumn({ projects, scopeSource }),
  }),
  column<Row>({
    header: () => "Status",
    data: ({ latestRevision }) => ({
      content: (
        <>
          <Tag text={latestRevision.status?.name || "Draft"} type={getTagTypeFromStatus(latestRevision.status?.code)} />
        </>
      ),
      value: latestRevision.status?.code,
    }),
  }),
  column<Row>({
    header: "Effective On",
    data: ({ latestRevision }) => dateCell(latestRevision.startDate, { dateFormatStyle: "long" }),
  }),
  column<Row>({
    header: "Expires On",
    data: ({ latestRevision }) => dateCell(latestRevision.endDate, { dateFormatStyle: "long" }),
  }),
];
